import DownloadIcon from '@mui/icons-material/GetApp'
import { IconButton, Tooltip, Typography } from '@mui/material'
import { useTranslate } from 'ra-core'
import { FunctionField, SimpleShowLayout, useRecordContext } from 'react-admin'

import { CreateBookingTransactionButton } from './buttons'
import config from './config'
import { useApi } from '../../api/apiProvider'
import AdvancedBooleanField from '../../components/AdvancedBooleanField'
import AdvancedTextField from '../../components/AdvancedTextField'
import CompactList from '../../components/CompactList'
import DateTooltipField from '../../components/DateTooltipField'
import PriceField from '../../components/PriceField'
import ReferenceValue from '../../components/ReferenceValue'
import StaticText from '../../components/StaticText'
import { BOOKING_TRANSACTION_ALL_TYPES, BOOKING_TRANSACTION_STATUSES } from '../../config/bookingTransactions'
import { useCommonStyles } from '../../config/theme'
import {
  isBookingTransactionTypeChargeBookingPrice,
  isBookingTransactionTypeChargeExtra,
} from '../../domain/bookingTransactions'
import { GenericListLayout, ListDatagrid } from '../common/list'
import { useShowStyles } from '../common/show'
import opsUsersConfig from '../opsUsers/config'
import OpsUserField from '../opsUsers/field'

const BookingTransactionPreAuthorisationRelatedField = (props) => (
  <AdvancedBooleanField
    {...props}
    source="is_pa_related"
    trueIcon={null}
    falseIcon={null}
    trueLabel="resources.booking-transactions.enums.isPaRelated.true"
    falseLabel="resources.booking-transactions.enums.isPaRelated.false"
  />
)

const BookingTransactionPanel = () => {
  const record = useRecordContext()
  const showClasses = useShowStyles()
  const commonClasses = useCommonStyles()
  return (
    <SimpleShowLayout sx={{ ...showClasses.fieldContainer, ...commonClasses.borderless }}>
      {(isBookingTransactionTypeChargeBookingPrice(record) || isBookingTransactionTypeChargeExtra(record)) && (
        <BookingTransactionPreAuthorisationRelatedField
          record={record}
          label="resources.booking-transactions.fields.is_pa_related"
        />
      )}
      <AdvancedTextField source="justification" />
      <AdvancedBooleanField source="handled_externally" falseIcon={null} trueIcon={null} />
    </SimpleShowLayout>
  )
}

const InvoiceField = ({ source }) => {
  const { [source]: invoiceId } = useRecordContext()
  const translate = useTranslate()
  const [getInvoiceData] = useApi(`/booking-transactions/invoice/${invoiceId}`)

  const downloadInvoice = async () => {
    if (!invoiceId) return
    const { result: invoiceData } = await getInvoiceData()
    if (!invoiceData) return
    const link = document.createElement('a')
    const filenamePrefix = translate(`resources.booking-transactions.fields.${source}`).toLowerCase()
    link.href = `data:application/pdf;base64,${invoiceData.content}`
    link.download = `${filenamePrefix}_${invoiceData.filename}`
    link.click()
  }

  return (
    <FunctionField
      source={source}
      render={() =>
        invoiceId ? (
          <Tooltip title={translate('resources.booking-transactions.list.downloadInvoice')} placement="left">
            <IconButton onClick={downloadInvoice} sx={{ color: 'primary.main' }} size="large">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Typography component="span" variant="body2" style={{ marginLeft: 13 }}>
            n/a
          </Typography>
        )
      }
    />
  )
}

const BookingTransactionsListLayout = ({ bookingId, ...props }) => {
  return (
    <GenericListLayout
      action={<CreateBookingTransactionButton bookingId={bookingId} />}
      compactListLayout={
        <CompactList
          icon={<config.icon />}
          title={() => (
            <>
              <Typography component="span" variant="body1" sx={{ display: 'inline' }} color="textPrimary">
                <AdvancedTextField source="type" map={BOOKING_TRANSACTION_ALL_TYPES} />
              </Typography>
              <Typography component="span" variant="body1" sx={{ display: 'inline' }} color="textSecondary">
                {' • '}
                <DateTooltipField source="created_on" addTime />
              </Typography>
            </>
          )}
          body={(record) => (
            <>
              <StaticText>{'resources.booking-transactions.fields.amount'}: </StaticText>
              <PriceField source="amount" />
              <br />
              <StaticText>{'resources.booking-transactions.fields.status'}: </StaticText>
              <AdvancedTextField source="status" map={BOOKING_TRANSACTION_STATUSES} />
              {Boolean(record[opsUsersConfig.options.referenceKey]) && (
                <>
                  <br />
                  <StaticText>{'resources.booking-transactions.fields.ops_user_id'} </StaticText>
                  <ReferenceValue reference={opsUsersConfig.name} />
                </>
              )}
              {(isBookingTransactionTypeChargeBookingPrice(record) || isBookingTransactionTypeChargeExtra(record)) && (
                <>
                  <br />
                  <StaticText>{'resources.booking-transactions.fields.is_pa_related'}: </StaticText>
                  <BookingTransactionPreAuthorisationRelatedField />
                </>
              )}
              <br />
              <StaticText>{'resources.booking-transactions.fields.justification'}: </StaticText>
              <AdvancedTextField source="justification" />
            </>
          )}
        />
      }
      regularListLayout={
        <ListDatagrid expand={<BookingTransactionPanel />}>
          <AdvancedTextField source="type" map={BOOKING_TRANSACTION_ALL_TYPES} />
          <DateTooltipField source="created_on" addTime />
          <PriceField source="amount" />
          <AdvancedTextField source="status" map={BOOKING_TRANSACTION_STATUSES} />
          <OpsUserField />
          <InvoiceField source="invoice_id" sortable={false} />
        </ListDatagrid>
      }
      {...props}
    />
  )
}

export default BookingTransactionsListLayout
